import React, { useState, useEffect, useRef } from 'react'
import { renderToString } from 'react-dom/server'
import { Layout, Card, Row, Col, Typography, Input, DatePicker, Button, Space, Table, App, Grid } from 'antd'
import dayjs from 'dayjs'
import {
  CarOutlined,
  FileTextOutlined,
  TagOutlined,
  UserOutlined,
  TeamOutlined,
  TruckOutlined,
  PrinterOutlined,
  ReloadOutlined,
  PictureOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import Navbar from '../../components/Navbar'
import Sidebar from '../../components/Sidebar'
import { useNavigate } from 'react-router-dom'
import ClientesListView from '../../components/ClientesTreeView'
import CDPracaGrid from '../../components/CDPracaGrid'
import PrinterUsageGrid from '../../components/PrinterUsageGrid'
import RelatorioGerencial from '../../relatorios/RelatorioGerencial'
import { buildFullHtml, openPrintWindow } from '../../relatorios/utils/print'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { notaanota } from '../../services/api'
import { getGlobalSelectedDate, setGlobalSelectedDate } from '../../utils/globalDate'
import OnboardPreviewModal from './components/OnboardPreviewModal'
import buildOnboardPreviewData from './components/OnboardPreviewAdapter'
import './PainelOperacional.css'

const { Content } = Layout
const { Title } = Typography



const PainelOperacional = ({ embedded = false }) => {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentTheme } = useTheme()
  const screens = Grid.useBreakpoint()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [empresa, setEmpresa] = useState(user?.assignedBranch?.codigo ?? user?.empresa ?? '') // Empresa do usuário logado
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString())
  const [dadosVeiculos, setDadosVeiculos] = useState([])
  const [dadosClientes, setDadosClientes] = useState([])
  const [loading, setLoading] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewRecord, setPreviewRecord] = useState(null)
  const [previewData, setPreviewData] = useState(null)
  // impressão direta do relatório gerencial
  const [statsData, setStatsData] = useState([
    { title: 'Veículos', value: 0, icon: <CarOutlined /> },
    { title: 'Notas\n(com impressão)', value: 0, icon: <FileTextOutlined /> },
    { title: 'Notas\n(sem impressão)', value: 0, icon: <FileTextOutlined /> },
    { title: 'Etiquetas\n(Impressas)', value: 0, icon: <TagOutlined /> },
    { title: 'Etiquetas\n(Não Impressas)', value: 0, icon: <TagOutlined /> },
    { title: 'Clientes', value: 0, icon: <UserOutlined /> }
  ])

  // Ref para controlar se a mensagem de sucesso já foi exibida
  const messageShownRef = useRef(false)

  // Atualizar horário a cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Atualizar empresa quando o usuário mudar
  useEffect(() => {
    const codigo = user?.assignedBranch?.codigo ?? user?.empresa
    if (codigo != null) {
      setEmpresa(codigo)
    }
  }, [user])

  // Evitar múltiplas requisições iniciais (React StrictMode)
  const initialFetchDoneRef = useRef(false)
  // Carregar dados iniciais quando a empresa estiver disponível
  useEffect(() => {
    if (empresa && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true
      buscarDadosBackend()
    }
  }, [empresa])

  // Escutar mudanças no estado da sidebar
  useEffect(() => {
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.collapsed)
    }

    window.addEventListener('sidebarToggle', handleSidebarToggle)
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle)
  }, [])

  // Removido: chamada automática adicional que duplicava requisição inicial

  // Função para alternar visibilidade da sidebar em mobile
  const handleMobileMenuClick = () => {
    setSidebarVisible(!sidebarVisible)
  }

  // Passar a função de menu mobile para o Navbar
  const navbarProps = {
    onMenuClick: screens.md ? undefined : handleMobileMenuClick
  }

  // Função para atualizar dados
  const handleRefresh = () => {
    messageShownRef.current = false // Reset flag para permitir nova mensagem
    buscarDadosBackend()
  }

  // Função para abrir galeria de imagens
  const handleImageGallery = () => {
    // Lógica para abrir galeria
  }

  const handleOpenOnboardPreview = (record) => {
    try {
      const nextPreviewData = buildOnboardPreviewData(record, empresa)
      setPreviewRecord(record)
      setPreviewData(nextPreviewData)
      setPreviewVisible(true)
    } catch (error) {
      console.error('Erro ao preparar preview do Onboard:', error)
      message.error('Não foi possível preparar o preview do Onboard')
    }
  }

  const handleCloseOnboardPreview = () => {
    setPreviewVisible(false)
  }

  // Gera o relatório gerencial e abre no navegador para impressão
  const handlePrintRelatorio = () => {
    try {
      const bodyHtml = renderToString(
        <RelatorioGerencial
          dadosClientes={dadosClientes}
          dadosVeiculos={dadosVeiculos}
          empresa={String(empresa || '')}
          dateLabel={selectedDate?.format ? selectedDate.format('DD/MM/YYYY') : ''}
          navbarInfo={{ empresa: user?.empresa, sigla: user?.sigla, nome: user?.nome }}
        />
      )
      const html = buildFullHtml({ title: 'Relatório Gerencial', bodyHtml })
      openPrintWindow(html)
    } catch (e) {
      console.error('Erro ao gerar relatório para impressão:', e)
      message.error('Falha ao gerar relatório para impressão')
    }
  }

  // Função unificada para buscar dados do backend
  const buscarDadosBackend = async (dataEspecifica = null) => {

    try {
      setLoading(true)

      // Usar a data específica, a data selecionada, a data global ou a data atual
      const dataParaConsulta = dataEspecifica || selectedDate || getGlobalSelectedDate()

      let dataFormatada

      if (dataParaConsulta) {
        dataFormatada = dataParaConsulta.format('YYYY-MM-DD')
      } else {
        // Se não há data selecionada, usar a data atual
        const hoje = new Date()
        const ano = hoje.getFullYear()
        const mes = String(hoje.getMonth() + 1).padStart(2, '0')
        const dia = String(hoje.getDate()).padStart(2, '0')
        dataFormatada = `${ano}-${mes}-${dia}`
      }

      // Fazer uma única requisição para o backend (onboard/dia)
      const responseOnboard = await notaanota.getOnboardPorDia(dataFormatada, empresa)
      console.log(responseOnboard)

      // Normalizar dados para componentes que esperam campos do formato antigo
      const dadosNormalizados = (Array.isArray(responseOnboard) ? responseOnboard : []).map(item => ({
        ...item,
        // Mapear REMETENTE -> CLIENTE para componentes
        CLIENTE: item.REMETENTE,
        // Garantir campos esperados por grids: CD e Rota (uppercase/lowercase compat)
        CD: item.CD ?? item.cd ?? '',
        Rota: item.Rota ?? item.rota ?? '',
        // Garantir campos de Nota/Série para contagens e renderização
        Nota: item.Nota ?? item.NOTA ?? null,
        Serie: item.Serie ?? item.SERIE ?? null,
        // Sinalizar status de etiqueta com base nos totais
        STATUS_ETIQUETA: (Number(item.STATUS) === 1 && (parseInt(item.TTL_ETIQUETAS_IMP) || 0) > 0) ? 1 : 0,
        // Usar última etiqueta como referência de data
        DATA_ETIQUETA: item.ULTIMA_ETIQUETA
      }))

      setDadosVeiculos(dadosNormalizados)
      setDadosClientes(dadosNormalizados)


      // Calcular estatísticas baseadas nos dados recebidos
      if (dadosNormalizados && dadosNormalizados.length > 0) {
        // Contar veículos únicos (CARRO distintos)

        const veiculosUnicos = new Set(dadosNormalizados.map(item => item.CARRO))
        const totalVeiculos = veiculosUnicos.size
        // Calcular notas com/sem impressão a partir do STATUS da nota
        const totalNotasImp = dadosNormalizados.reduce((sum, item) => sum + (Number(item.STATUS) === 1 ? 1 : 0), 0)
        const totalNotasSem = dadosNormalizados.reduce((sum, item) => sum + (Number(item.STATUS) === 0 ? 1 : 0), 0)
        const totalEtiquetasImp = dadosNormalizados.reduce((sum, item) => sum + (parseInt(item.TTL_ETIQUETAS_IMP) || 0), 0)
        const totalEtiquetasSem = dadosNormalizados.reduce((sum, item) => sum + (parseInt(item.TTL_ETIQUETAS_SEM) || 0), 0)
        const totalClientes = new Set(dadosNormalizados.map(item => item.REMETENTE)).size
        // Agrupar coletas FINALIZADA por CARRO e contar por veículo
        const coletasFinalizadasPorCarro = dadosNormalizados.reduce((acc, item) => {
          const statusNum = Number(item.STATUS_COLETA)
          if (statusNum === 1) {
            const carro = item.CARRO || 'Sem Veículo'
            acc[carro] = (acc[carro] || 0) + 1
          }
          return acc
        }, {})
        // Quantidade de CARROS que possuem ao menos uma coleta FINALIZADA
        const totalColetasFim = Object.keys(coletasFinalizadasPorCarro).length

        setStatsData([
          { title: 'Veículos', value: totalVeiculos, icon: <CarOutlined /> },
          { title: 'Notas\n(com impressão)', value: totalNotasImp, icon: <FileTextOutlined /> },
          { title: 'Notas\n(sem impressão)', value: totalNotasSem, icon: <FileTextOutlined /> },
          { title: 'Etiquetas\n(Impressas)', value: totalEtiquetasImp, icon: <TagOutlined /> },
          { title: 'Etiquetas\n(Não Impressas)', value: totalEtiquetasSem, icon: <TagOutlined /> },
          { title: 'Clientes', value: totalClientes, icon: <UserOutlined /> },
          { title: 'Coletas\nFinalizadas', value: totalColetasFim, icon: <UserOutlined /> }
        ])

        // Exibir mensagem apenas se não foi exibida ainda
        if (!messageShownRef.current) {
          //message.success(`Dados carregados com sucesso! ${totalVeiculos} veículos encontrados.`)
          messageShownRef.current = true
        }
      } else {
        // Limpar tabelas quando não há dados
        setDadosVeiculos([])
        setDadosClientes([])

        // Resetar estatísticas se não houver dados
        setStatsData([
          { title: 'Veículos', value: 0, icon: <CarOutlined /> },
          { title: 'Notas\n(com impressão)', value: 0, icon: <FileTextOutlined /> },
          { title: 'Notas\n(sem impressão)', value: 0, icon: <FileTextOutlined /> },
          { title: 'Etiquetas\n(Impressas)', value: 0, icon: <TagOutlined /> },
          { title: 'Etiquetas\n(Não Impressas)', value: 0, icon: <TagOutlined /> },
          { title: 'Clientes', value: 0, icon: <UserOutlined /> },
          { title: 'Coletas\nFinalizadas', value: 0, icon: <UserOutlined /> }
        ])

      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error)

      // Limpar tabelas e cards em caso de erro
      setDadosVeiculos([])
      setDadosClientes([])
      setStatsData([
        { title: 'Veículos', value: 0, icon: <CarOutlined /> },
        { title: 'Notas\n(com impressão)', value: 0, icon: <FileTextOutlined /> },
        { title: 'Notas\n(sem impressão)', value: 0, icon: <FileTextOutlined /> },
        { title: 'Etiquetas\n(Impressas)', value: 0, icon: <TagOutlined /> },
        { title: 'Etiquetas\n(Não Impressas)', value: 0, icon: <TagOutlined /> },
        { title: 'Clientes', value: 0, icon: <UserOutlined /> }
      ])

      message.error('Erro ao carregar dados: ' + (error.message || 'Erro desconhecido'))
    } finally {
      setLoading(false)
    }
  }

  // Função para atualizar a data selecionada e buscar dados
  const handleDateChange = (date) => {

    setSelectedDate(date)
    setGlobalSelectedDate(date)
    // Buscar dados automaticamente quando a data mudar
    if (date) {
      buscarDadosBackend(date)
    }
  }

  const navigationItems = [
    { title: 'Clientes', icon: <TeamOutlined /> },
    { title: 'Roteirização', icon: <TruckOutlined /> },
    { title: 'Impressoras', icon: <PrinterOutlined /> }
  ]

  // Renderização embutida: retorna apenas header + conteúdo
  if (embedded) {
    return (
      <>
        <div className="content-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 24px',
          borderBottom: currentTheme === 'dark' ? '1px solid #333333' : '1px solid #f0f0f0',
          backgroundColor: currentTheme === 'dark' ? '#111111' : undefined
        }}>
          <Title level={2} style={{
            margin: 0,
            color: currentTheme === 'dark' ? '#ffffff' : undefined
          }}>Painel Operacional</Title>

          <Space size="middle" wrap style={{ alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="empresa-label" style={{
                fontSize: '14px',
                color: currentTheme === 'dark' ? '#ffffff' : undefined
              }}>Empresa:</span>
              <Input
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                style={{
                  width: '70px',
                  backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined,
                  borderColor: currentTheme === 'dark' ? '#475569 !important' : undefined,
                  color: currentTheme === 'dark' ? '#ffffff !important' : undefined
                }}
                styles={{
                  input: {
                    color: currentTheme === 'dark' ? '#ffffff !important' : undefined,
                    backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined
                  }
                }}
                size="small"
              />
            </div>

            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              placeholder="Selecionar data"
              size="small"
              style={{
                width: '140px',
                backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined,
                borderColor: currentTheme === 'dark' ? '#475569 !important' : undefined,
                color: currentTheme === 'dark' ? '#ffffff !important' : undefined
              }}
              styles={{
                input: {
                  color: currentTheme === 'dark' ? '#ffffff !important' : undefined,
                  backgroundColor: currentTheme === 'dark' ? '#1e293b !important' : undefined
                }
              }}
            />

            <Button
              type="primary"
              icon={<ClockCircleOutlined />}
              onClick={handleRefresh}
              loading={loading}
              size="small"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px'
              }}
            >
              {currentTime} Atualizar
            </Button>

            <Button
              icon={<PictureOutlined />}
              onClick={handleImageGallery}
              size="small"
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            />
            <Button
              id="relatorio-gerencial"
              icon={<PrinterOutlined />}
              onClick={handlePrintRelatorio}
              size="small"
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            />

          </Space>
        </div>

        <div className="content-body">
          {/* Cards de Estatísticas */}
          <Row
            gutter={[8, 8]}
            className="stats-cards"
            style={{ display: 'flex', flexWrap: screens.md ? 'nowrap' : 'wrap' }}
          >
            {statsData.map((stat, index) => (
              <Col
                key={index}
                flex={screens.md ? '0 0 14.285%' : undefined}
                xs={12}
                sm={12}
              >
                <Card
                  className="stats-card"
                  variant="elevated"
                  styles={{ body: { padding: 8 } }}
                  style={{ textAlign: 'center' }}
                >
                  <div
                    className="stats-number"
                    style={{
                      fontSize: 24,
                      lineHeight: 1.5,
                      fontWeight: 600,
                      color:
                        (typeof stat.title === 'string' && (stat.title.includes('Não Impressas') || stat.title.includes('sem impressão')) && Number(stat.value) > 0)
                          ? '#ff4d4f'
                          : (typeof stat.title === 'string' && stat.title.includes('Finalizadas') && Number(stat.value) > 0)
                            ? '#135200'
                            : undefined
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="stats-title"
                    style={{ fontSize: 11, lineHeight: 1.2, whiteSpace: 'pre-line' }}
                  >
                    {stat.title}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Veículos */}
          <div style={{ marginTop: '24px' }}>
            <Card
              title="Veículos do Dia"
              variant="elevated"
              className="veiculos-card"
              style={{
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
            >
              {Array.isArray(dadosVeiculos) && dadosVeiculos.length > 0 ? (
                (() => {
                  const dadosVeiculosArray = Array.isArray(dadosVeiculos) ? dadosVeiculos : []
                  const parseDecimal = (value) => {
                    if (value === null || value === undefined || value === '') return 0
                    if (typeof value === 'number') return Number.isFinite(value) ? value : 0
                    const valueString = String(value).trim()
                    if (!valueString) return 0
                    let normalizedValue = valueString
                    if (valueString.includes(',') && valueString.includes('.')) {
                      normalizedValue = valueString.replace(/\./g, '').replace(',', '.')
                    } else if (valueString.includes(',')) {
                      normalizedValue = valueString.replace(',', '.')
                    }
                    const parsed = Number(normalizedValue)
                    return Number.isFinite(parsed) ? parsed : 0
                  }
                  const veiculosAgrupados = dadosVeiculosArray.reduce((acc, item) => {
                    const chave = `${item.CARRO}_${item.ROMANEIO}`
                    if (!acc[chave]) {
                      acc[chave] = {
                        CARRO: item.CARRO,
                        ROMANEIO: item.ROMANEIO,
                        EMPRESA: item.EMPRESA || empresa,
                        ORIGEM_VINCULO: String(item.ORIGEM_VINCULO || item.origem_vinculo || 'CARRO').toUpperCase(),
                        DATA_INICIO: item.PRIMEIRA_ETIQUETA || null,
                        DATA_FIM: item.ULTIMA_ETIQUETA || null,
                        STATUS: item.STATUS_COLETA,
                        TTL_ETIQUETAS_IMP: 0,
                        TTL_ETIQUETAS_SEM: 0,
                        TOTAL_PESO: 0,
                        TOTAL_M3: 0,
                        PESO_VEICULO: 0,
                        M3_VEICULO: 0,
                        CLIENTES_SET: new Set(),
                        items: []
                      }
                    }
                    acc[chave].TTL_ETIQUETAS_IMP += parseInt(item.TTL_ETIQUETAS_IMP) || 0
                    acc[chave].TTL_ETIQUETAS_SEM += parseInt(item.TTL_ETIQUETAS_SEM) || 0
                    acc[chave].TOTAL_PESO += parseDecimal(item.PESO ?? item.peso)
                    acc[chave].TOTAL_M3 += parseDecimal(item.M3 ?? item.m3)
                    acc[chave].PESO_VEICULO = parseDecimal(item.PESO_VEICULO ?? acc[chave].PESO_VEICULO)
                    acc[chave].M3_VEICULO = parseDecimal(item.M3_VEICULO ?? acc[chave].M3_VEICULO)
                    acc[chave].CLIENTES_SET.add(item.REMETENTE)
                    {
                      const first = item.PRIMEIRA_ETIQUETA || null
                      const last = item.ULTIMA_ETIQUETA || null
                      if (first && (!acc[chave].DATA_INICIO || first < acc[chave].DATA_INICIO)) {
                        acc[chave].DATA_INICIO = first
                      }
                      if (last && (!acc[chave].DATA_FIM || last > acc[chave].DATA_FIM)) {
                        acc[chave].DATA_FIM = last
                      }
                    }
                    if (Number(item.STATUS_COLETA) === 1) {
                      acc[chave].STATUS = 1
                    } else if (typeof acc[chave].STATUS === 'undefined' || acc[chave].STATUS === null) {
                      acc[chave].STATUS = Number(item.STATUS_COLETA) || 0
                    }
                    if (String(item.ORIGEM_VINCULO || item.origem_vinculo || '').toUpperCase() === 'USUARIO') {
                      acc[chave].ORIGEM_VINCULO = 'USUARIO'
                    }
                    acc[chave].items.push(item)
                    return acc
                  }, {})
                  const veiculosArray = Object.values(veiculosAgrupados)
                    .sort((a, b) => {
                      const carroA = String(a.CARRO || '')
                      const carroB = String(b.CARRO || '')
                      const carroComparison = carroA.localeCompare(carroB)
                      if (carroComparison !== 0) return carroComparison
                      const romaneioA = String(a.ROMANEIO || '')
                      const romaneioB = String(b.ROMANEIO || '')
                      return romaneioA.localeCompare(romaneioB)
                    })
                    .map(v => ({
                      ...v,
                      TTL_CLIENTES: (v.CLIENTES_SET ? v.CLIENTES_SET.size : 0)
                    }))
                  const veiculosArraySafe = Array.isArray(veiculosArray) ? veiculosArray : []
                  const dataSource = veiculosArraySafe.map((veiculo, index) => {
                    const extrairHora = (dataHora) => {
                      if (!dataHora) return ''
                      const s = typeof dataHora === 'string' ? dataHora : String(dataHora)
                      const m = s.match(/(\d{2}):(\d{2})/)
                      return m ? `${m[1]}:${m[2]}` : ''
                    }
                    const primeiraHora = extrairHora(veiculo.DATA_INICIO)
                    const ultimaHora = extrairHora(veiculo.DATA_FIM)
                    const etiquetasImpressas = veiculo.TTL_ETIQUETAS_IMP
                    const etiquetasSemImpressao = veiculo.TTL_ETIQUETAS_SEM || 0
                    return {
                      key: `${veiculo.CARRO}_${veiculo.ROMANEIO}`,
                      veiculo: String(veiculo.CARRO || ''),
                      empresa: String(veiculo.EMPRESA || empresa || ''),
                      origemVinculo: String(veiculo.ORIGEM_VINCULO || 'CARRO').toUpperCase(),
                      romaneio: String(veiculo.ROMANEIO || ''),
                      items: Array.isArray(veiculo.items) ? veiculo.items : [],
                      primeiraHora,
                      ultimaHora,
                      coletaStatus: Number(veiculo.STATUS || 0),
                      etiquetasImpressas,
                      etiquetasSemImpressao,
                      totalClientes: veiculo.TTL_CLIENTES,
                      totalPeso: veiculo.TOTAL_PESO || 0,
                      totalM3: veiculo.TOTAL_M3 || 0,
                      pesoVeiculo: veiculo.PESO_VEICULO || 0,
                      m3Veiculo: veiculo.M3_VEICULO || 0
                    }
                  })
                  const columns = [
                    {
                      title: 'Veículo',
                      dataIndex: 'veiculo',
                      key: 'veiculo',
                      onCell: (record) => ({
                        onClick: () => handleOpenOnboardPreview(record),
                        style: { cursor: 'pointer' }
                      }),
                      render: (text, record) => (
                        <button
                          type="button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            width: '100%',
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            textAlign: 'left'
                          }}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            handleOpenOnboardPreview(record)
                          }}
                        >
                          {record?.origemVinculo === 'USUARIO' ? (
                            <UserOutlined
                              className={Number(record?.coletaStatus) === 1 ? 'blink-icon' : ''}
                              style={{ color: '#1890ff' }}
                            />
                          ) : (
                            <TruckOutlined
                              className={Number(record?.coletaStatus) === 1 ? 'blink-icon' : ''}
                              style={{ color: '#1890ff' }}
                            />
                          )}
                          <strong>{text}</strong>
                        </button>
                      ),
                      sorter: (a, b) => String(a.veiculo || '').localeCompare(String(b.veiculo || '')),
                      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
                    },

                    {
                      title: 'Etiquetas',
                      key: 'totalEtiquetas',
                      render: (_, record) => {
                        const total = (record.etiquetasImpressas || 0) + (record.etiquetasSemImpressao || 0)
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <TagOutlined style={{ color: '#595959' }} />
                            <strong>{total}</strong>
                          </div>
                        )
                      },
                      sorter: (a, b) => (
                        ((a.etiquetasImpressas || 0) + (a.etiquetasSemImpressao || 0)) -
                        ((b.etiquetasImpressas || 0) + (b.etiquetasSemImpressao || 0))
                      ),
                      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
                    },
                    {
                      title: 'Primeira',
                      dataIndex: 'primeiraHora',
                      key: 'primeiraHora',
                      render: (text) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ClockCircleOutlined style={{ color: '#52c41a' }} />
                          {text}
                        </div>
                      ),
                      sorter: (a, b) => {
                        const converterHorario = (horarioStr) => {
                          if (!horarioStr) return 0
                          const [horas, minutos] = horarioStr.split(':').map(Number)
                          return horas * 60 + minutos
                        }
                        const horarioA = converterHorario(a.primeiraHora)
                        const horarioB = converterHorario(b.primeiraHora)
                        return horarioA - horarioB
                      },
                      responsive: ['md', 'lg', 'xl']
                    },
                    {
                      title: 'Última',
                      dataIndex: 'ultimaHora',
                      key: 'ultimaHora',
                      render: (text) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ClockCircleOutlined style={{ color: '#fa8c16' }} />
                          {text}
                        </div>
                      ),
                      sorter: (a, b) => {
                        const converterHorario = (horarioStr) => {
                          if (!horarioStr) return 0
                          const [horas, minutos] = horarioStr.split(':').map(Number)
                          return horas * 60 + minutos
                        }
                        const horarioA = converterHorario(a.ultimaHora)
                        const horarioB = converterHorario(b.ultimaHora)
                        return horarioA - horarioB
                      },
                      responsive: ['md', 'lg', 'xl']
                    },
                    {
                      title: 'Etiquetas Impressas',
                      dataIndex: 'etiquetasImpressas',
                      key: 'etiquetasImpressas',
                      render: (text) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <PrinterOutlined style={{ color: '#52c41a' }} />
                          <strong style={{ color: '#52c41a' }}>{text}</strong>
                        </div>
                      ),
                      sorter: (a, b) => a.etiquetasImpressas - b.etiquetasImpressas,
                      responsive: ['md', 'lg', 'xl']
                    },
                    {
                      title: 'Falta Imprimir',
                      dataIndex: 'etiquetasSemImpressao',
                      key: 'etiquetasSemImpressao',
                      render: (text) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TagOutlined style={{ color: text > 0 ? '#ff4d4f' : '#52c41a' }} />
                          <span style={{ color: text > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>
                            {text}
                          </span>
                        </div>
                      ),
                      sorter: (a, b) => a.etiquetasSemImpressao - b.etiquetasSemImpressao,
                      responsive: ['sm', 'md', 'lg', 'xl']
                    },
                    {
                      title: 'Clientes',
                      dataIndex: 'totalClientes',
                      key: 'totalClientes',
                      render: (text) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <UserOutlined style={{ color: '#722ed1' }} />
                          {text}
                        </div>
                      ),
                      sorter: (a, b) => a.totalClientes - b.totalClientes,
                      responsive: ['sm', 'md', 'lg', 'xl']
                    }
                  ]

                  return (
                    <Table
                      dataSource={dataSource}
                      columns={columns}
                      pagination={{
                        pageSize: screens.xs ? 5 : 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} de ${total} veículos`,
                        simple: screens.xs
                      }}
                      size={screens.xs ? 'small' : 'middle'}
                      scroll={{ x: screens.xs ? 600 : 800 }}
                      onRow={(record) => ({
                        onClick: () => { },
                        style: { cursor: 'pointer' }
                      })}
                      rowClassName={(record, index) =>
                        index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
                      }
                    />
                  )
                })()
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#999'
                }}>
                  <TruckOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                  <p>Nenhum veículo encontrado para a data selecionada</p>
                </div>
              )}
            </Card>
          </div>

          {/* Seção Clientes do Dia */}
          <div style={{ marginTop: '12px', width: '100%' }}>
            <ClientesListView
              dadosClientes={dadosClientes}
              loading={loading}
            />

            {/* Seção CD/Praça/Cliente */}
            <CDPracaGrid data={dadosClientes} loading={loading} />

            {/* Seção Impressoras */}
            <PrinterUsageGrid data={dadosClientes} loading={loading} />
          </div>

        </div>
        {previewVisible ? (
          <OnboardPreviewModal
            open={previewVisible}
            onClose={handleCloseOnboardPreview}
            previewData={previewData}
            previewRecord={previewRecord}
            theme={currentTheme}
          />
        ) : null}
      </>
    )
  }

  return (
    <div className="painel-operacional">
      <Navbar {...navbarProps} />
      <Layout style={{ marginTop: 40 }}>
        <Sidebar
          visible={sidebarVisible}
          onClose={() => setSidebarVisible(false)}
          onMenuClick={({ key }) => {
            if (key === 'operacional') navigate('/painel-operacional')
            if (key === 'expedicao') navigate('/painel-expedicao')
            if (key === 'relatorios') navigate('/relatorios')
          }}
        />

      </Layout>
      {previewVisible ? (
        <OnboardPreviewModal
          open={previewVisible}
          onClose={handleCloseOnboardPreview}
          previewData={previewData}
          previewRecord={previewRecord}
          theme={currentTheme}
        />
      ) : null}
    </div>
  )
};

export default PainelOperacional;
